import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LuChevronDown } from "react-icons/lu";
import { FiArrowLeft, FiMail } from "react-icons/fi";
import Button from "../components/ui/Button";

export default function FaqPage() {
    const [expandedIndex, setExpandedIndex] = useState(null);

    const faqs = [
        {
            question: "¿Cómo compro entradas?",
            answer: "Selecciona el evento de tu interés, elige los asientos deseados, proporciona tus datos personales y completa el pago. Recibirás las entradas por email inmediatamente después.",
        },
        {
            question: "¿Puedo cambiar mis asientos después de comprar?",
            answer: "Los cambios de asientos están sujetos a disponibilidad. Contacta con nuestro equipo de soporte para solicitar un cambio. Se pueden aplicar tarifas adicionales.",
        },
        {
            question: "¿Cuáles son los métodos de pago aceptados?",
            answer: "Aceptamos tarjetas de crédito y débito (Visa, Mastercard, Amex) a través de Stripe y Redsys. El pago es seguro y encriptado.",
        },
        {
            question: "¿Puedo transferir mis entradas a otra persona?",
            answer: "Las entradas son personales y no transferibles. El comprador debe asistir al evento con la entrada que compró.",
        },
        {
            question: "¿Cuándo recibiré mis entradas?",
            answer: "Recibirás un email de confirmación con tus entradas inmediatamente después de completar el pago. También puedes descargarlas en PDF desde tu confirmación de pedido.",
        },
        {
            question: "¿Hay descuentos disponibles?",
            answer: "Ocasionalmente ofrecemos códigos de descuento. Puedes aplicarlos durante el checkout si tienes un código válido.",
        },
        {
            question: "¿Puedo obtener un reembolso?",
            answer: "La política de reembolsos depende del evento específico. Algunos eventos ofrecen reembolsos completos si se cancelan antes de una fecha determinada. Revisa la información del evento para detalles específicos.",
        },
        {
            question: "¿Qué hago si no recibo mis entradas?",
            answer: "Primero revisa tu carpeta de spam. Si aún no las encuentras, contacta con nuestro equipo de soporte en entradas@pulseticket.es proporcionando tu número de pedido.",
        },
        {
            question: "¿Puedo imprimir mis entradas en casa?",
            answer: "Sí, puedes descargar tus entradas en PDF e imprimirlas. Alternativamente, puedes mostrar el email de confirmación o el código de barras en tu teléfono en la puerta.",
        },
        {
            question: "¿Hay algún requisito de edad para asistir?",
            answer: "Los requisitos de edad dependen del evento. Revisa la información específica del evento que deseas asistir. Algunos eventos requieren que las personas menores de edad vayan acompañadas de un adulto.",
        },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto px-4 py-8"
        >
            <div className="mb-8">
                <Link to="/">
                    <Button variant="ghost" size="sm" icon={FiArrowLeft}>
                        Volver
                    </Button>
                </Link>
            </div>

            <div className="mb-12">
                <h1 className="text-4xl font-display font-bold text-white dark:text-white mb-2">
                    Preguntas Frecuentes
                </h1>
                <p className="text-slate-400 dark:text-slate-400 text-lg font-medium">
                    Encuentra respuestas a las preguntas más comunes sobre la
                    compra y uso de entradas.
                </p>
            </div>

            <div className="space-y-3">
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        initial={false}
                        className="border border-slate-700/50 dark:border-slate-700/50 rounded-lg overflow-hidden hover:shadow-lg dark:hover:shadow-xl dark:hover:shadow-black/40 transition bg-slate-800/60 dark:bg-slate-800/60 backdrop-blur-sm"
                    >
                        <button
                            onClick={() =>
                                setExpandedIndex(
                                    expandedIndex === index ? null : index,
                                )
                            }
                            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/60 dark:hover:bg-slate-700/60 transition-colors"
                        >
                            <h3 className="text-left font-semibold text-white dark:text-white pr-4">
                                {faq.question}
                            </h3>
                            <motion.div
                                animate={{
                                    rotate: expandedIndex === index ? 180 : 0,
                                }}
                                transition={{ duration: 0.2 }}
                                className="flex-shrink-0"
                            >
                                <LuChevronDown
                                    className={`transition-colors ${expandedIndex === index ? "text-primary-400 dark:text-primary-400" : "text-slate-400 dark:text-slate-400"}`}
                                    size={20}
                                />
                            </motion.div>
                        </button>

                        <motion.div
                            initial={false}
                            animate={{
                                height: expandedIndex === index ? "auto" : 0,
                                opacity: expandedIndex === index ? 1 : 0,
                            }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden bg-slate-700/40 dark:bg-slate-700/40 border-t border-slate-700/50 dark:border-slate-700/50"
                        >
                            <p className="px-6 py-4 text-slate-300 dark:text-slate-300 leading-relaxed">
                                {faq.answer}
                            </p>
                        </motion.div>
                    </motion.div>
                ))}
            </div>

            <div className="mt-12 p-8 bg-gradient-to-br from-slate-800 via-blue-900/30 to-slate-900 dark:from-slate-800 dark:via-blue-900/30 dark:to-slate-900 border-2 border-slate-700/50 dark:border-slate-700/50 rounded-xl backdrop-blur-sm">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary-500/20 dark:bg-primary-500/20 text-primary-400 dark:text-primary-400 rounded-lg flex items-center justify-center border border-primary-500/40 dark:border-primary-500/40">
                        <FiMail size={24} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white dark:text-white mb-2 text-lg">
                            ¿No encontraste lo que buscas?
                        </h3>
                        <p className="text-slate-400 dark:text-slate-400 mb-4 font-medium">
                            Si tienes una pregunta que no se responde aquí,
                            nuestro equipo de soporte está disponible para
                            ayudarte.
                        </p>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() =>
                                (window.location.href =
                                    "mailto:entradas@pulseticket.es")
                            }
                        >
                            Enviar Correo
                        </Button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
